import React, { useState } from "react"
import { Button, InputNumber } from "antd"
import {
  GRADE_OPTIONS,
  PRIMARY_BTN_STYLE,
  OUTLINE_BTN_STYLE,
  schoolGradePopulations,
} from "./constants"

const GradePicker = ({ wizardMeta, answered, onRespond }) => {
  const {
    suggestedGrades,
    validGrades,
    isUniversal,
    documentStudentCounts,
    documentGradePopulations,
  } = wizardMeta
  const gradeOptions = validGrades && validGrades.length > 0 ? validGrades : GRADE_OPTIONS
  const populations = schoolGradePopulations()

  const [selectedGrades, setSelectedGrades] = useState(suggestedGrades || [])
  // Seeded per grade, per field (can_be_supported / being_supported independently) from THIS
  // document only: documentGradePopulations (its own per-grade breakdown) first, falling back
  // to documentStudentCounts (one aggregate total for the whole activity) only when the
  // per-grade breakdown doesn't cover that grade/field. Deliberately does NOT fall back to the
  // matched existing record's own previously-saved counts — silently blending old dashboard
  // numbers in next to this upload's data made it impossible to tell which number came from
  // where; a grade/field the document doesn't cover starts blank instead. Editing a field just
  // overrides whichever starting value it got seeded from.
  const [counts, setCounts] = useState(() => {
    const pick = (field, fromDocument) => {
      if (fromDocument && fromDocument[field] != null) return fromDocument[field]
      return documentStudentCounts && documentStudentCounts[field]
    }

    return (suggestedGrades || []).reduce((seeded, grade) => {
      const fromDocument = documentGradePopulations && documentGradePopulations[grade]
      seeded[grade] = {
        can_be_supported: pick("can_be_supported", fromDocument),
        being_supported: pick("being_supported", fromDocument),
      }
      return seeded
    }, {})
  })

  const toggleGrade = (grade) => {
    if (answered) return
    setSelectedGrades((prev) => (prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]))
  }

  const updateCount = (grade, field, value) => {
    setCounts((prev) => ({
      ...prev,
      [grade]: { ...prev[grade], [field]: value },
    }))
  }

  // Can't claim to support more students than the grade's real population, and can't claim
  // to actually be supporting more than were said to be supportable in the first place.
  const canBeSupportedInvalid = (grade) => {
    const population = populations[grade]
    const value = counts[grade] && counts[grade].can_be_supported
    return (
      Number.isFinite(population)
      && Number.isFinite(value)
      && value > population
    )
  }

  const beingSupportedInvalid = (grade) => {
    const canBeSupported = counts[grade] && counts[grade].can_be_supported
    const value = counts[grade] && counts[grade].being_supported
    return (
      Number.isFinite(canBeSupported)
      && Number.isFinite(value)
      && value > canBeSupported
    )
  }

  const hasErrors = !isUniversal
    && selectedGrades.some(
      (grade) => canBeSupportedInvalid(grade) || beingSupportedInvalid(grade),
    )

  // Same order as the grade buttons above, rather than whatever order grades were added in
  // (e.g. suggested-from-document order) — keeps the recap line and the fields below it from
  // ever looking out of sync with the button row.
  const orderedSelectedGrades = gradeOptions.filter((grade) => selectedGrades.includes(grade))

  // A Universal activity is open to every student, so counts aren't typed in — they're each
  // selected grade's own configured population, same as the Add Activity form's Universal branch.
  const totalPopulation = selectedGrades.reduce(
    (total, grade) => total + (populations[grade] || 0),
    0,
  )

  const handleSubmit = () => {
    const grades = selectedGrades.map((grade) => (isUniversal
      ? {
        grade,
        students_can_be_supported: populations[grade] || 0,
        students_being_supported: populations[grade] || 0,
      }
      : {
        grade,
        students_can_be_supported:
              (counts[grade] && counts[grade].can_be_supported) || 0,
        students_being_supported:
              (counts[grade] && counts[grade].being_supported) || 0,
      }))
    onRespond({ grades: selectedGrades, internalSelectedGrades: grades })
  }

  // A grade this school hasn't configured has no real population to track at all, so it's never
  // offered as something to force-add — always go straight to the real picker below, which only
  // ever lets the user choose from gradeOptions (this school's own configured grade levels).

  return (
    <div className="wizard-grade-picker" style={{ marginTop: 8 }}>
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        {gradeOptions.map((grade) => (
          <button
            key={grade}
            type="button"
            disabled={answered}
            onClick={() => toggleGrade(grade)}
            style={
              selectedGrades.includes(grade)
                ? {
                  ...PRIMARY_BTN_STYLE,
                  height: 30,
                  minWidth: 36,
                  padding: "0 8px",
                }
                : {
                  ...OUTLINE_BTN_STYLE,
                  height: 30,
                  minWidth: 36,
                  padding: "0 8px",
                }
            }
          >
            {grade}
          </button>
        ))}
      </div>

      {isUniversal && selectedGrades.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 16,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, color: "#5a6a7a" }}>
            {"Students can be supported: "}
            <strong>{totalPopulation}</strong>
          </div>
          <div style={{ fontSize: 13, color: "#5a6a7a" }}>
            {"Students being supported: "}
            <strong>{totalPopulation}</strong>
          </div>
        </div>
      )}

      {!isUniversal && orderedSelectedGrades.length > 0 && (
        <div
          style={{
            fontSize: 13,
            color: "#5a6a7a",
            marginBottom: 8,
          }}
        >
          <strong>Selected grades: </strong>
          {orderedSelectedGrades.join(", ")}
        </div>
      )}

      {!isUniversal
        && orderedSelectedGrades.map((grade) => {
          const population = populations[grade]
          const canBeSupported = counts[grade] && counts[grade].can_be_supported
          return (
            <div key={grade} style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#333",
                  marginBottom: 4,
                }}
              >
                {grade}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#5a6a7a",
                      display: "block",
                      marginBottom: 2,
                    }}
                  >
                    Students can be supported:
                  </label>
                  <InputNumber
                    min={0}
                    controls={false}
                    disabled={answered}
                    value={canBeSupported}
                    onChange={(v) => updateCount(grade, "can_be_supported", v)}
                    style={{ width: "100%" }}
                  />
                  {canBeSupportedInvalid(grade) && (
                    <div style={{ color: "#d4380d", fontSize: 12 }}>
                      {`Can't exceed this grade's population of ${population}.`}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#5a6a7a",
                      display: "block",
                      marginBottom: 2,
                    }}
                  >
                    Students being supported:
                  </label>
                  <InputNumber
                    min={0}
                    controls={false}
                    disabled={answered}
                    value={counts[grade] && counts[grade].being_supported}
                    onChange={(v) => updateCount(grade, "being_supported", v)}
                    style={{ width: "100%" }}
                  />
                  {beingSupportedInvalid(grade) && (
                    <div style={{ color: "#d4380d", fontSize: 12 }}>
                      Can&apos;t exceed students supported.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}

      {!answered && (
        <Button
          style={{ ...PRIMARY_BTN_STYLE, marginTop: 10, width: "100%" }}
          disabled={selectedGrades.length === 0 || hasErrors}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      )}
    </div>
  )
}

export default GradePicker
